/* eslint-disable no-mixed-spaces-and-tabs */
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import Button from "@ingka/button";
import InlineMessage from "@ingka/inline-message";
import Search from "@ingka/search";
import Checkbox from "@ingka/checkbox";
import Toggle from "@ingka/toggle";
import { userType } from "./utils";
import pencil from "@ingka/ssr-icon/paths/pencil";
import globe from "@ingka/ssr-icon/paths/globe";
import sortAscending from "@ingka/ssr-icon/paths/sort-ascending";
import sortDescending from "@ingka/ssr-icon/paths/sort-descending";
import { getImage } from "../../utils/markets";
import Avatar from "@ingka/avatar";
import SSRIcon from "@ingka/ssr-icon";
import Modal, { ModalFooter, Prompt } from "@ingka/modal";
import PillGroup from "@ingka/pill-group";
import { PillProps } from "@ingka/pill";

import {
	ColumnDef,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	useReactTable,
	CellContext,
	HeaderContext,
	getSortedRowModel,
	SortingState,
} from "@tanstack/react-table";

import { ApplicationUiSchema } from "../../services/applications/types";
import { UserData, ApplicationOnboardedUsers, UserApplicationData } from "../../services/users";
import { LoadingWrapper, StyledCommercialMessage, ActionsWrapper, SaveButtonWrapper, SrOnly } from "../styles";
import ManageUsersModal from "./ManageUserModal.tsx/ManageUsersModal";
import { TableWrapper, StyledModalHeader } from "../styles";
import { Application } from "../../store/types";
import { LoadingComponent } from "../LoadingLayout";
import LoadingAnnouncer from "../LoadingAnnouncer";
import useLoadingAnnouncer from "../LoadingAnnouncer/useLoadingAnnouncer";
import UserPagiantion from "./UserPagination";
import useFilterUsers from "../../hooks/useFilterUsers";
import useAnnounceFilterResults from "../../hooks/useAnnounceFilterResults";

const pageSize = 10;

const StyledMessage = styled(StyledCommercialMessage)`
	margin-left: 0.5rem;
`;

const StyledTableWrapper = styled(TableWrapper)`
	margin-top: 20px;
	border-right: transparent;
	border-left: transparent;
	border-top: transparent;
	.table-head,
	.table-cell {
		text-align: unset;
		margin: 0;
		padding: 0.5rem;
		border-right: transparent !important;
		border-left: transparent !important;
		background-color: white;
	}
`;

const StyledSearch = styled(Search)`
	display: flex;
`;

const StyledToggle = styled(Toggle)`
	margin-top: 1.5rem;
	margin-bottom: 2.5rem;
	display: flex;
	flex-grow: 0.5;
`;

const MarketImage = styled.img`
	height: 25px;
	width: 25px;
	margin-right: 0.2rem;
`;

const StyledMarketIconWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
	justify-content: center;
	align-items: center;
`;

const StyledHeaderWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 2rem;
`;

const StyledHeader = styled.h1`
	padding-top: 0.3rem;
`;

const StyledFooter = styled.div`
	display: flex;
	gap: 1rem;
	padding: 0.5rem;
	justify-content: end;
`;

const StyledAvatarWrapper = styled.div`
	display: flex;
	justify-content: center;
`;

const StyledTableHeader = styled.th<{ $canSort?: boolean }>`
	cursor: ${(props) => (props?.$canSort ? "pointer" : "default")};
`;

const StyledPillGroup = styled(PillGroup)`
	.pill {
		margin-right: 0.5rem;
	}
`;

interface ManageUsersLayoutProps {
	loading: boolean;
	showModal: boolean;
	setShowModal: (value: boolean) => void;
	modalHeading: string;
	setModalHeading: (value: string) => void;
	modalButtonLabel: string;
	setModalButtonLabel: (value: string) => void;
	setSelectedUser: (data?: UserApplicationData) => void;
	addingNewUser: boolean;
	setAddingNewUser: (value: boolean) => void;
	onFinalSubmit: () => void;
	isAdmin: boolean;
	currentUserSelected: boolean;
	usersInApplication: ApplicationOnboardedUsers[];
	toastMessage: string;
	setToastMessage: (value: string) => void;
	handleClearURLProps: () => void;
	appUsersLoading: boolean;
	setAppUsersLoading: (value: boolean) => void;
	activeApplication: Application;
	onRemoveUserAccess: () => void;
	toggleUserTypeBtnIndex?: number;
	setToggleUserTypeBtnIndex: Dispatch<SetStateAction<number | undefined>>;
	setSearchUserValue: (value: string) => void;
	setSelectedMarkets: (value?: string[]) => void;
	hasChangedUser: boolean;
	selectedUser?: UserApplicationData;
	data: UserData[];
	onboardingUser: boolean;
	setOnboardingUser: (value: boolean) => void;
	showRemoveUsersModal: boolean;
	setShowRemoveUsersModal: (value: boolean) => void;
	onRemoveMultipleUserAccess: (value: any[]) => void;
	removeUsersLoading: boolean;
	nbrOfusers: number;
	marketsData?: string[];
	uiSchemas?: ApplicationUiSchema[];
	selectedMarkets?: string[];
}

const Layout: FC<ManageUsersLayoutProps> = ({
	loading,
	data,
	usersInApplication,
	setShowModal,
	showModal,
	modalHeading,
	setModalHeading,
	modalButtonLabel,
	setModalButtonLabel,
	marketsData,
	uiSchemas,
	setSelectedUser,
	selectedUser,
	addingNewUser,
	setAddingNewUser,
	onFinalSubmit,
	isAdmin,
	currentUserSelected,
	toastMessage,
	setToastMessage,
	handleClearURLProps,
	appUsersLoading,
	activeApplication,
	onRemoveUserAccess,
	toggleUserTypeBtnIndex,
	setToggleUserTypeBtnIndex,
	setSearchUserValue,
	selectedMarkets,
	setSelectedMarkets,
	hasChangedUser,
	onboardingUser,
	setOnboardingUser,
	showRemoveUsersModal,
	setShowRemoveUsersModal,
	onRemoveMultipleUserAccess,
	removeUsersLoading,
	nbrOfusers,
}) => {
	const [rowSelection, setRowSelection] = useState({});
	const [sorting, setSorting] = useState<SortingState>([
		{
			id: "name-column",
			desc: false,
		},
	]);
	const [currentSearchTerm, setCurrentSearchTerm] = useState("");

	useEffect(() => {
		setRowSelection({});
		setSorting([
			{
				id: "name-column",
				desc: false,
			},
		]);
	}, []);

	const columns = useMemo<ColumnDef<ApplicationOnboardedUsers>[]>(
		() => [
			{
				id: "select",
				header: ({ table }: HeaderContext<ApplicationOnboardedUsers, unknown>) => (
					<Checkbox
						id="selectAll"
						value="select-all"
						checked={table.getIsAllRowsSelected()}
						indeterminate={table.getIsSomeRowsSelected()}
						onChange={table.getToggleAllRowsSelectedHandler()}
						title="Select all users"
						aria-label="Select all user"
					/>
				),
				cell: ({ row }: CellContext<ApplicationOnboardedUsers, unknown>) => (
					<Checkbox
						id={`select-user-${row.id}`}
						value="select-user"
						checked={row.getIsSelected()}
						disabled={!row.getCanSelect()}
						indeterminate={row.getIsSomeSelected()}
						onChange={row.getToggleSelectedHandler()}
						title="Select user"
						aria-label="Select user"
					/>
				),
			},
			{
				header: "Avatar",
				id: "avatar-column",
				enableSorting: false,
				accessorKey: "name",
				cell: ({ row }: CellContext<ApplicationOnboardedUsers, unknown>) => {
					const rowValue = row.getValue("avatar-column") as string;
					const userName = rowValue?.split("(")[0]?.split(" ");
					return (
						<StyledAvatarWrapper>
							<Avatar
								screenReaderText={rowValue}
								text={`${userName?.[0].substring(0, 1)}${userName?.[1].substring(0, 1)}`}
							/>
						</StyledAvatarWrapper>
					);
				},
			},
			{
				header: "Name",
				accessorKey: "name",
				id: "name-column",
				cell: ({ row }: CellContext<ApplicationOnboardedUsers, unknown>) => {
					const rowValue = row.getValue("name-column") as string;
					return <strong>{rowValue?.split("(")[0]}</strong>;
				},
			},
			{
				header: "Role",
				accessorKey: `applications.${activeApplication?.id}`,
				id: "role-column",
				enableSorting: false,
				cell: ({ row }: CellContext<ApplicationOnboardedUsers, unknown>) => {
					const rowValue = row.getValue("role-column") as any;
					if (rowValue?.permissions === "admin") {
						return <StyledMessage message="Admin" variant="family" subtle />;
					} else if (rowValue?.markets && Object.keys(rowValue.markets).length) {
						return <StyledMessage message="Maintainer" variant="nlp" subtle />;
					} else {
						return <StyledMessage message="Read Only" variant="new" subtle />;
					}
				},
			},
			{
				header: "Mail",
				id: "email-column",
				accessorKey: "email",
			},
			{
				header: "Markets",
				id: "markets-column",
				enableSorting: false,
				accessorKey: `applications.${activeApplication?.id}`,
				cell: ({ row }: CellContext<ApplicationOnboardedUsers, unknown>) => {
					const rowValue = row.getValue("markets-column") as any;
					if (rowValue?.permissions === "admin") {
						return <SSRIcon paths={globe} viewBox="0 0 24 24" role="img" ariaHidden={false} aria-label="Global" />;
					} else if (rowValue?.markets && typeof rowValue.markets === "object") {
						const marketKeys = Object.keys(rowValue.markets);
						const visibleMarkets = marketKeys.slice(0, 5);
						const remainingCount = marketKeys.length - 5;

						const markets = visibleMarkets.map((marketKey) => (
							<MarketImage
								key={marketKey}
								src={getImage(marketKey) || getImage("xx")}
								title={marketKey}
								alt={`Market: ${marketKey}`}
								loading="lazy"
							/>
						));

						return (
							<StyledMarketIconWrapper>
								{markets}
								{remainingCount > 0 && (
									<span
										aria-label={`And ${remainingCount} more market${remainingCount === 1 ? "" : "s"}`}
										title={`And ${remainingCount} more market${remainingCount === 1 ? "" : "s"}`}
									>
										+{remainingCount}
									</span>
								)}
							</StyledMarketIconWrapper>
						);
					} else {
						return <span aria-label="No markets assigned">-</span>;
					}
				},
			},
			{
				header: "Action",
				id: "action-column",
				enableSorting: false,
				accessorKey: "id",
				cell: ({ row }: CellContext<ApplicationOnboardedUsers, unknown>) => {
					const rowValue = row.getValue("action-column") as string;
					return (
						<div>
							<Button
								text="Edit"
								size="small"
								iconPosition="leading"
								type="tertiary"
								ssrIcon={pencil}
								onClick={() => {
									setModalHeading("Update User");
									setModalButtonLabel("Update User");
									setSelectedUser(data?.find((userData) => userData.id === rowValue));
									setAddingNewUser(false);
									setOnboardingUser(false);
									setShowModal(true);
								}}
								aria-haspopup="dialog"
							/>
						</div>
					);
				},
			},
		],
		[
			activeApplication?.id,
			data,
			setAddingNewUser,
			setModalButtonLabel,
			setModalHeading,
			setOnboardingUser,
			setSelectedUser,
			setShowModal,
		]
	);

	const table = useReactTable({
		data: usersInApplication,
		columns,
		initialState: {
			pagination: {
				pageSize,
			},
		},
		state: {
			rowSelection,
			sorting,
		},
		onSortingChange: setSorting,
		enableRowSelection: true,
		onRowSelectionChange: setRowSelection,
		getSortedRowModel: getSortedRowModel(),
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	});

	const pillsData: PillProps[] = useMemo(
		() =>
			Object.values(userType).map((userType: string, index) => ({
				label: userType,
				selected: index === toggleUserTypeBtnIndex,
				size: "xsmall",
			})),
		[toggleUserTypeBtnIndex]
	);

	const { pills, handlePills } = useFilterUsers(pillsData, setToggleUserTypeBtnIndex);

	const activeFilterLabel = useMemo(() => {
		const activePill = pills.find((pill) => pill.selected);
		return activePill?.label || "All";
	}, [pills]);

	const filteredRowCount = table.getFilteredRowModel().rows.length;

	const announceRef = useAnnounceFilterResults(filteredRowCount, currentSearchTerm, activeFilterLabel as string);

	const handleSearch = (value: string) => {
		setCurrentSearchTerm(value);
		setSearchUserValue(value);
	};

	useLoadingAnnouncer("manageUsers", loading || appUsersLoading);

	return (
		<>
			<LoadingAnnouncer loadingMessage="Loading users" completedMessage="Loading completed" />

			<SrOnly ref={announceRef} role="status" aria-live="polite" aria-atomic="true" />

			<StyledHeaderWrapper>
				<StyledHeader>Total Users Onboarded ({nbrOfusers})</StyledHeader>
				{data?.length > 0 && (
					<StyledSearch
						id="search"
						clearBtnText="Clear search"
						submitBtnText="Apply search"
						onSearch={(_, { value }) => handleSearch(value)}
						onChange={(_, { value }) => handleSearch(value)}
						onClear={() => handleSearch("")}
					/>
				)}
			</StyledHeaderWrapper>

			{loading || appUsersLoading ? (
				<LoadingComponent loadingText="Loading Users" />
			) : (
				<>
					{data?.length === 0 ? (
						<LoadingWrapper>
							<InlineMessage
								title="No Users"
								body={"Seems like you don't have any users onboarded"}
								variant="informative"
								headingLevel="2"
							/>
						</LoadingWrapper>
					) : (
						<>
							<StyledPillGroup id="user-requests-filter" list={pills} onChange={handlePills} withCarousel />

							<StyledTableWrapper>
								<table
									className="table"
									id="usersOnboardedTable"
									aria-label={`Users table, showing ${filteredRowCount} user${filteredRowCount === 1 ? "" : "s"}`}
									aria-rowcount={filteredRowCount}
								>
									<thead>
										{table.getHeaderGroups().map((headerGroup) => (
											<tr key={headerGroup.id}>
												{headerGroup.headers.map((header) => (
													<StyledTableHeader
														key={header.id}
														colSpan={header.colSpan}
														scope="col"
														aria-sort={
															!header.column.getCanSort()
																? undefined
																: header.column.getIsSorted() === "asc"
																? "ascending"
																: header.column.getIsSorted() === "desc"
																? "descending"
																: "none"
														}
														onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
														onKeyDown={
															header.column.getCanSort()
																? (e) => {
																		if (e.key === "Enter" || e.key === " ") {
																			header.column.getToggleSortingHandler()?.(e);
																		}
																  }
																: undefined
														}
														tabIndex={header.column.getCanSort() ? 0 : undefined}
														title={
															header.column.getCanSort()
																? header.column.getNextSortingOrder() === "asc"
																	? "Sort ascending"
																	: header.column.getNextSortingOrder() === "desc"
																	? "Sort descending"
																	: "Clear sort"
																: undefined
														}
														$canSort={header.column.getCanSort()}
													>
														{flexRender(header.column.columnDef.header, header.getContext())}
														{{
															asc: <SSRIcon paths={sortAscending} viewBox="0 0 24 24" aria-hidden="true" />,
															desc: <SSRIcon paths={sortDescending} viewBox="0 0 24 24" aria-hidden="true" />,
														}[header.column.getIsSorted() as string] ?? null}
													</StyledTableHeader>
												))}
											</tr>
										))}
									</thead>
									<tbody>
										{table.getRowModel().rows.length === 0 ? (
											<tr>
												<td colSpan={columns.length} style={{ textAlign: "center" }}>
													No users found.
												</td>
											</tr>
										) : (
											table.getRowModel().rows.map((row) => (
												<tr key={row.id}>
													{row.getVisibleCells().map((cell) =>
														cell.column.id === "name-column" ? (
															<th key={cell.id} scope="row" style={{ textAlign: "center" }}>
																{flexRender(cell.column.columnDef.cell, cell.getContext())}
															</th>
														) : (
															<td key={cell.id} id={cell.id} style={{ textAlign: "center" }}>
																{flexRender(cell.column.columnDef.cell, cell.getContext())}
															</td>
														)
													)}
												</tr>
											))
										)}
									</tbody>
								</table>
								{usersInApplication.length > pageSize && (
									<StyledFooter>
										<UserPagiantion data={table} />
									</StyledFooter>
								)}
							</StyledTableWrapper>
							<ActionsWrapper>
								<SaveButtonWrapper>
									<Button
										onClick={() => {
											setModalHeading("Add new User");
											setModalButtonLabel("Add User");
											setAddingNewUser(true);
											setOnboardingUser(false);
											setShowModal(true);
										}}
										text="Onboard new user"
										type="emphasised"
										title="Onboard new user"
									/>
									<Button
										text="Remove users"
										data-testid="revmoveUsers"
										onClick={() => setShowRemoveUsersModal(true)}
										type="danger"
										title="Remove user(s)"
										disabled={table.getSelectedRowModel().flatRows.length === 0}
									/>
								</SaveButtonWrapper>
							</ActionsWrapper>
							<ManageUsersModal
								showModal={showModal}
								setShowModal={setShowModal}
								modalHeading={modalHeading}
								setModalHeading={setModalHeading}
								modalButtonLabel={modalButtonLabel}
								setModalButtonLabel={setModalButtonLabel}
								marketsData={marketsData}
								uiSchemas={uiSchemas}
								setSelectedUser={setSelectedUser}
								selectedUser={selectedUser}
								addingNewUser={addingNewUser}
								setAddingNewUser={setAddingNewUser}
								onFinalSubmit={onFinalSubmit}
								isAdmin={isAdmin}
								currentUserSelected={currentUserSelected}
								toastMessage={toastMessage}
								setToastMessage={setToastMessage}
								usersInApplication={usersInApplication}
								handleClearURLProps={handleClearURLProps}
								selectedUserEmail={selectedUser?.email}
								onRemoveUserAccess={onRemoveUserAccess}
								selectedMarkets={selectedMarkets}
								setSelectedMarkets={setSelectedMarkets}
								hasChangedUser={hasChangedUser}
								onboardingUser={onboardingUser}
							/>
							<Modal
								visible={showRemoveUsersModal}
								handleCloseBtn={() => setShowRemoveUsersModal(false)}
								escapable={false}
							>
								<Prompt
									header={<StyledModalHeader title="Confirmation!" />}
									footer={
										<ModalFooter>
											<Button text="Close" type="emphasised" onClick={() => setShowRemoveUsersModal(false)} />
											<Button
												data-testid="RemoveUsers"
												text="Remove user(s)"
												loading={removeUsersLoading}
												onClick={() => onRemoveMultipleUserAccess(table.getSelectedRowModel().flatRows)}
											/>
										</ModalFooter>
									}
									title="Remove users"
									titleId="removeUsersTitleId"
								>
									<p>
										Do you want to remove selected user(s) access to <strong>{activeApplication!.name}</strong>?
									</p>
								</Prompt>
							</Modal>
						</>
					)}
				</>
			)}
		</>
	);
};

export default Layout;
